// The Observer's Acasă, as the wireframe draws it: one band, and nothing else.
//
// `wireframes/react/src/pages/Acasa.tsx:146-165` — a role that does not report gets
// the informational band, its organisation as the detail, and two actions: view
// stocks, and the dashboards. No greeting, no card grid; the wireframe has those on
// no role.
//
// The alert inbox this band used to advertise is gone with owner decision D7
// (docs/decizii-paritate-wireframe-2026-09-05.md): the wireframe's observer menu has no
// such entry and no caiet clause grants one — UC-ALRT-01 gives the alert dashboard to
// the System Administrator, UC-ALRT-07 delivers to everyone else by e-mail and webhook.
import { useTranslate } from '@tolgee/react';
import { DayStatusBand, RouterLink } from '@simsm/ui-shared';
import { MudButton } from '@egov-moldova/mud-react';
import { useSession } from '../../hooks/use-session';
import { useRoleLabel } from '../../hooks/use-observer-roles';
import { useObserverScope } from '../../features/observer/use-observer-scope';
import { ObserverScopeBanner } from '../../components/AppShell/ObserverScopeBanner';

export function ObserverHome(): JSX.Element {
  const { t } = useTranslate();
  const sessionQuery = useSession();
  const session = sessionQuery.data;

  // caiet UC61-03: building the key from the role code named a role the product ships
  // copy for and printed `role.observer_app` at anything the System Administrator added
  // later. The shared resolver falls back to the display name that administrator gave.
  //
  // The wireframe's detail here is the INSTITUTION (`ROLES[role].org`, session.ts:13-14 —
  // "CNAM", "Ministerul Sanatatii"), and SIMSM stores no such field: the columns of
  // `analytics.observer_role_definition` are role_code, display_name, description,
  // medicine_tags, registrar_kinds, active, and its display_name is the ROLE's name
  // ("Observator CNAM"). The institution exists only as prose inside `description`.
  // Until a column carries it, the role label is what can be said truthfully. See
  // `specs/038-home-and-observer/spec.md §A-22` for what would have to change.
  const roleLabelFor = useRoleLabel();
  const roleLabel = session?.activeRole ? roleLabelFor(session.activeRole) : t('role.observer');

  // The wireframe gives every one of its observers the national title, CNAM included
  // (Acasa.tsx:150). The gate that used to withhold it from a restricted role read the
  // banner below as a contradiction, and it is not: the banner says WHICH MEDICINES the
  // role may read (`observer_role_definition.medicine_tags`), the title says AT WHICH
  // LEVELS it may aggregate them. The two axes are independent, and this application
  // grants every observer both levels — `components/StockChartTable/stock-filters.ts`,
  // pinned at `stock-filters.spec.ts:17-19`: `levelsFor('OBSERVER_CNAM')` is
  // `['region', 'national']`, identical to OBSERVER and OBSERVER_MS. The wireframe's own
  // model agrees (`scopRol().agregari`, geo.ts:53).
  //
  // The one role the wide title would lie to is the one that reads nothing at all, so
  // `noAccess` keeps the plain title. While the scope is still loading the wide title is
  // the default, because it is what every observer but that one ends on.
  const { scope } = useObserverScope(session?.activeRole);
  const national = scope === undefined || !scope.noAccess;

  return (
    <section className="simsm-home simsm-home--single">
      <ObserverScopeBanner />
      <DayStatusBand
        status="info"
        title={t(national ? 'home.band.observer.title_national' : 'home.band.observer.title')}
        detail={roleLabel}
        actions={
          <>
            <RouterLink to="/observer/stocks">
              <MudButton shape="circular" variant="primary" size="md">
                {t('home.band.observer.action_stocks')}
              </MudButton>
            </RouterLink>
            <RouterLink to="/observer/dashboards">
              <MudButton shape="circular" appearance="text" variant="strict" size="md">
                {t('home.band.observer.action_dashboards')}
              </MudButton>
            </RouterLink>
          </>
        }
      />
    </section>
  );
}
